import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { findFirstNode } from '../../../components/Tree/helpers/helpers';
import { createReferenceEqualSelector } from '../../selectors/utils';
import { setMarket } from '../intl';
import type { RootState } from '../..';
import type { NodeBase, NodeBaseItem } from '../../../entities/Node';

interface SortedCategory {
  positionBeforeChange: number;
}

interface TreeSortState {
  sortedCatalogs: Record<string, Record<string, Record<string, SortedCategory>>>;
  originalTree: NodeBase[];
}

const initialState: TreeSortState = {
  sortedCatalogs: {},
  originalTree: [],
};

const treeSortSlice = createSlice({
  name: 'treeSort',
  initialState,
  reducers: {
    saveSortOrder(
      state,
      action: PayloadAction<{
        catalogId: string;
        parentId: string;
        categoryId: string;
        currentTree: NodeBase[];
        newTree: NodeBase[];
      }>,
    ) {
      const { catalogId, parentId, categoryId, currentTree, newTree } =
        action.payload;

      const currentCatalog = state.sortedCatalogs[catalogId] || {};
      const currentParent = currentCatalog[parentId] || {};
      const currentCategory = currentParent[categoryId] || null;

      // Set original tree on first move
      if (state.originalTree.length === 0) {
        state.originalTree = currentTree;
      }

      const originalParent = findFirstNode({
        treeData: state.originalTree,
        searchMethod: (n: NodeBaseItem) => n.node.id === parentId,
        ignoreCollapsed: false,
      }) as unknown as NodeBaseItem | null;

      // Check each category in parent if it's the same as original position and delete
      if (Object.keys(currentParent).length && originalParent?.node?.children) {
        const newParent = findFirstNode({
          treeData: newTree,
          searchMethod: (n: NodeBaseItem) => n.node.id === parentId,
          ignoreCollapsed: false,
        }) as unknown as NodeBaseItem | null;

        const categories = Object.keys(currentParent);

        originalParent.node.children.forEach(
          (originalChild: NodeBase, index: number) => {
            if (
              originalChild.categoryId &&
              originalChild.categoryId ===
                newParent?.node?.children?.[index]?.categoryId &&
              categories.includes(originalChild.categoryId)
            ) {
              delete state.sortedCatalogs[catalogId][parentId][
                originalChild.categoryId
              ];
            }
          },
        );
      }

      if (currentCategory === null && originalParent?.node?.children) {
        const positionBeforeChange = originalParent.node.children.findIndex(
          (f: NodeBase) => f.categoryId === categoryId,
        );

        state.sortedCatalogs = {
          ...state.sortedCatalogs,
          [catalogId]: {
            ...state.sortedCatalogs[catalogId],
            [parentId]: {
              ...(state.sortedCatalogs[catalogId]?.[parentId] || {}),
              [categoryId]: {
                positionBeforeChange,
              },
            },
          },
        };
      }

      // Clear parentNode if empty
      if (
        currentCategory &&
        state.sortedCatalogs[catalogId]?.[parentId] &&
        Object.keys(state.sortedCatalogs[catalogId][parentId]).length === 0
      ) {
        delete state.sortedCatalogs[catalogId][parentId];
      }
      if (
        currentCategory &&
        state.sortedCatalogs[catalogId] &&
        Object.keys(state.sortedCatalogs[catalogId]).length === 0
      ) {
        delete state.sortedCatalogs[catalogId];
        state.originalTree = [];
      }
    },
    resetTreeSorting(state, action: PayloadAction<{ catalogId: string }>) {
      const { catalogId } = action.payload;
      state.originalTree = [];
      delete state.sortedCatalogs[catalogId];
    },
    resetSortOrder(state) {
      state.sortedCatalogs = {};
    },
    clearSortingForCatalog(state, action: PayloadAction<string>) {
      const catalogId = action.payload;
      delete state.sortedCatalogs[catalogId];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setMarket, () => ({ ...initialState }));
  },
});

export const {
  saveSortOrder,
  resetTreeSorting,
  resetSortOrder,
  clearSortingForCatalog,
} = treeSortSlice.actions;

export default treeSortSlice.reducer;

// Selectors
const selectTreeSort = (state: RootState) => state.treeSort;

export const selectSortedCatalogs = createReferenceEqualSelector(
  selectTreeSort,
  (state) => state.sortedCatalogs,
);

export const selectOriginalTree = createReferenceEqualSelector(
  selectTreeSort,
  (state) => state.originalTree,
);

export const selectHasUnsavedSorting = createReferenceEqualSelector(
  selectTreeSort,
  (state) => Object.keys(state.sortedCatalogs).length > 0,
);

export const selectSortedCatalogsForCatalog = createReferenceEqualSelector(
  selectTreeSort,
  (_state: RootState, catalogId: string) => catalogId,
  (state, catalogId) => state.sortedCatalogs[catalogId] || null,
);
